import { useEffect, useRef } from 'react'
import useWebSocket from 'react-use-websocket'
import { wsUrl } from '@/v2/constants/requests'
import usePageVisibility from '@/v2/hooks/usePageVisibility'

const RealTime = ({
  channel,
  onEvent,
  onReconnect,
  keepAliveInBackground = false,
  ...connectionProps
}) => {
  const isVisible = usePageVisibility()
  const subscriptionId = JSON.stringify({ channel, ...connectionProps })
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent
  const onReconnectRef = useRef(onReconnect)
  onReconnectRef.current = onReconnect
  const hasConnectedRef = useRef(false)

  const { lastJsonMessage, readyState, sendMessage } = useWebSocket(
    wsUrl,
    {
      share: true,
      shouldReconnect: () => true,
      // Never give up: a deploy or a dyno restart outlasts any fixed number of
      // attempts, and a page that stops trying stays silent until a refresh.
      // The interval below keeps the retries at one every 30s at most.
      reconnectAttempts: Infinity,
      reconnectInterval: attemptNumber =>
        Math.min(1000 * 2 ** attemptNumber, 30000),
      filter: ({ data }) => {
        try {
          const parsed = JSON.parse(data)
          const { type, identifier } = parsed
          if (type === 'disconnect') {
            console.log(parsed)
          }
          if (
            type === 'ping' ||
            type === 'welcome' ||
            type === 'confirm_subscription' ||
            type === 'disconnect'
          ) {
            return false
          }
          return identifier === subscriptionId
        } catch (e) {
          console.error('WebSocket filter parse error:', e)
          return false
        }
      },
    },
    // Hidden tabs drop the socket to spare phones, but a subscriber that must
    // be heard from the background (the new-message sound) can opt out.
    keepAliveInBackground || isVisible
  )

  useEffect(() => {
    if (readyState === 1) {
      console.log(`WebSocket connected to ${channel}.`, connectionProps)
      sendMessage(
        JSON.stringify({
          command: 'subscribe',
          identifier: JSON.stringify({
            channel,
            ...connectionProps,
          }),
        })
      )

      // Anything broadcast while the socket was down is gone for good, so
      // the screen fetches its data again every time it comes back.
      if (hasConnectedRef.current) onReconnectRef.current?.()
      hasConnectedRef.current = true
    }
  }, [readyState])

  useEffect(() => {
    if (!lastJsonMessage) return

    const { message } = lastJsonMessage
    console.log(`${channel}:`, message)

    onEventRef.current && onEventRef.current(message)
  }, [lastJsonMessage, channel])

  return null
}

export default RealTime
